'use client'

import { useState, useRef, useCallback, useEffect } from 'react'
import { Upload, X, CheckCircle, AlertCircle, Loader2, FileIcon, ImageIcon, FileVideo, FileAudio } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Progress } from '@/components/ui/progress'
import { useI18n } from '@/lib/hooks/use-i18n'
import { uploadNotebookFile, type NotebookUploadedFile } from '@/lib/api/notebook-upload'
import { cn } from '@/lib/utils'

// ── Types ───────────────────────────────────────────────────────────────────

interface UploadItem {
  id: string
  file: File
  progress: number // 0-100
  status: 'pending' | 'uploading' | 'done' | 'error'
  error?: string
  result?: NotebookUploadedFile
  previewUrl?: string
}

export interface NotebookUploadZoneProps {
  dir: string // e.g. "notebook-source"
  onFilesUploaded?: (files: NotebookUploadedFile[]) => void
  className?: string
  compact?: boolean // smaller padding for use inside dialogs
}

// ── Helpers ─────────────────────────────────────────────────────────────────

function generateId() {
  return `upload-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
}

function getFileIcon(file: File) {
  const ext = file.name.split('.').pop()?.toLowerCase() || ''
  if (['jpg','jpeg','png','gif','bmp','svg','ico','tiff','tif','webp'].includes(ext)) {
    return ImageIcon
  }
  if (['mp4','avi','mov','wmv','flv','mkv','webm','m4v'].includes(ext)) {
    return FileVideo
  }
  if (['mp3','wav','aac','ogg','flac','wma','m4a'].includes(ext)) {
    return FileAudio
  }
  return FileIcon
}

function createFilePreview(file: File): string | undefined {
  if (file.type.startsWith('image/')) {
    return URL.createObjectURL(file)
  }
  return undefined
}

// ── Component ───────────────────────────────────────────────────────────────

export function NotebookUploadZone({
  dir,
  onFilesUploaded,
  className,
  compact = false,
}: NotebookUploadZoneProps) {
  const { t } = useI18n()
  const [items, setItems] = useState<UploadItem[]>([])
  const itemsRef = useRef<UploadItem[]>([])
  const [isDragOver, setIsDragOver] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const processingRef = useRef(false)
  const completedRef = useRef<NotebookUploadedFile[]>([])

  // Keep itemsRef in sync
  const updateItems = useCallback((updater: (prev: UploadItem[]) => UploadItem[]) => {
    setItems((prev) => {
      const next = updater(prev)
      itemsRef.current = next
      return next
    })
  }, [])

  // ── Add files to the queue ──────────────────────────────────────────────

  const addFiles = useCallback((files: FileList | File[]) => {
    const fileArray = Array.from(files)
    if (fileArray.length === 0) return

    updateItems((prev) => {
      const newItems: UploadItem[] = []
      for (const file of fileArray) {
        // Skip duplicates (by name + size)
        if (prev.some((item) => item.file.name === file.name && item.file.size === file.size)) {
          continue
        }
        newItems.push({
          id: generateId(),
          file,
          progress: 0,
          status: 'pending',
          previewUrl: createFilePreview(file),
        })
      }
      return [...newItems, ...prev]
    })

    // Auto-start upload if not already processing
    if (!processingRef.current) {
      setTimeout(() => processNext(), 50)
    }
  }, [updateItems])

  // ── Process upload queue (one at a time) ────────────────────────────────

  const processNext = useCallback(async () => {
    if (processingRef.current) return
    processingRef.current = true

    const currentItems = itemsRef.current
    const pendingIndex = currentItems.findIndex((item) => item.status === 'pending')
    if (pendingIndex === -1) {
      processingRef.current = false

      // All done — fire callback if any completed
      if (completedRef.current.length > 0) {
        onFilesUploaded?.(completedRef.current)
        completedRef.current = []
      }
      return
    }

    const itemId = currentItems[pendingIndex].id
    const file = currentItems[pendingIndex].file

    // Mark as uploading
    updateItems((prev) =>
      prev.map((item) =>
        item.id === itemId ? { ...item, status: 'uploading' as const } : item,
      ),
    )

    try {
      const result = await uploadNotebookFile(file, dir, (progress) => {
        updateItems((prev) =>
          prev.map((item) =>
            item.id === itemId ? { ...item, progress: Math.max(item.progress, progress) } : item,
          ),
        )
      })

      // Mark as done
      updateItems((prev) =>
        prev.map((item) =>
          item.id === itemId
            ? { ...item, status: 'done' as const, progress: 100, result }
            : item,
        ),
      )

      completedRef.current.push(result)
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error)
      updateItems((prev) =>
        prev.map((item) =>
          item.id === itemId
            ? { ...item, status: 'error' as const, error: message }
            : item,
        ),
      )
    }

    processingRef.current = false

    // Process next item
    setTimeout(() => processNext(), 100)
  }, [dir, onFilesUploaded, updateItems])

  // ── Remove item ─────────────────────────────────────────────────────────

  const removeItem = useCallback((itemId: string) => {
    updateItems((prev) => {
      const item = prev.find((i) => i.id === itemId)
      if (item?.previewUrl) {
        URL.revokeObjectURL(item.previewUrl)
      }
      return prev.filter((i) => i.id !== itemId)
    })
  }, [updateItems])

  // ── Clear all done/error items ──────────────────────────────────────────

  const clearCompleted = useCallback(() => {
    updateItems((prev) => {
      for (const item of prev) {
        if (item.previewUrl) URL.revokeObjectURL(item.previewUrl)
      }
      return prev.filter((i) => i.status === 'pending' || i.status === 'uploading')
    })
  }, [updateItems])

  // ── Drag-and-drop handlers ──────────────────────────────────────────────

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragOver(true)
  }, [])

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragOver(false)
  }, [])

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragOver(false)
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      addFiles(e.dataTransfer.files)
    }
  }, [addFiles])

  // ── File input handler ──────────────────────────────────────────────────

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      addFiles(e.target.files)
      e.target.value = ''
    }
  }, [addFiles])

  const handleClick = useCallback(() => {
    fileInputRef.current?.click()
  }, [])

  // ── Counters ────────────────────────────────────────────────────────────

  const doneCount = items.filter((i) => i.status === 'done').length
  const errorCount = items.filter((i) => i.status === 'error').length
  const activeCount = items.filter(
    (i) => i.status === 'pending' || i.status === 'uploading',
  ).length
  const hasActiveItems = activeCount > 0

  // ── Render ──────────────────────────────────────────────────────────────

  const acceptStr = '.pdf,.doc,.docx,.pptx,.ppt,.xlsx,.xls,.txt,.md,.epub,.mp4,.avi,.mov,.wmv,.mp3,.wav,.m4a,.aac,.jpg,.jpeg,.png,.tiff,.zip,.tar,.gz,.html,.csv'

  return (
    <div className={cn('flex flex-col gap-3', className)}>
      {/* Drop zone */}
      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={handleClick}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleClick() }}
        className={cn(
          'relative flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed cursor-pointer transition-colors',
          'hover:border-primary/50 hover:bg-primary/5',
          compact ? 'p-4' : 'p-6',
          isDragOver
            ? 'border-primary bg-primary/10'
            : 'border-muted-foreground/25 bg-muted/30',
        )}
      >
        <Upload
          className={cn(
            'h-8 w-8 transition-colors',
            isDragOver ? 'text-primary' : 'text-muted-foreground',
          )}
        />
        <div className="text-center">
          <p className="text-sm font-medium">
            {isDragOver ? t('notebooks.upload.dropHint') : t('notebooks.upload.zoneTitle')}
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            {t('notebooks.upload.zoneDescription')}
          </p>
        </div>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          className="hidden"
          accept={acceptStr}
          onChange={handleFileSelect}
        />
      </div>

      {/* Upload queue */}
      {items.length > 0 && (
        <div className="space-y-2 max-h-48 overflow-y-auto">
          {items.map((item) => (
            <UploadItemRow
              key={item.id}
              item={item}
              onRemove={removeItem}
              t={t}
              compact={compact}
            />
          ))}
        </div>
      )}

      {/* Action bar */}
      {(doneCount > 0 || errorCount > 0) && !hasActiveItems && (
        <div className="flex items-center justify-between">
          <span className="text-xs text-muted-foreground">
            {doneCount > 0 && (
              <span className="text-green-600">
                {t('notebooks.upload.completedCount', { count: doneCount })}
              </span>
            )}
            {doneCount > 0 && errorCount > 0 && ' · '}
            {errorCount > 0 && (
              <span className="text-destructive">
                {t('notebooks.upload.failedCount', { count: errorCount })}
              </span>
            )}
          </span>
          <Button variant="ghost" size="sm" onClick={clearCompleted}>
            {t('notebooks.upload.clearCompleted')}
          </Button>
        </div>
      )}
    </div>
  )
}

// ── Upload Item Row ────────────────────────────────────────────────────────

function UploadItemRow({
  item,
  onRemove,
  t,
  compact,
}: {
  item: UploadItem
  onRemove: (id: string) => void
  t: (key: string, params?: Record<string, unknown>) => string
  compact?: boolean
}) {
  const Icon = getFileIcon(item.file)

  const statusIcon = (() => {
    switch (item.status) {
      case 'uploading':
        return <Loader2 className="h-4 w-4 animate-spin text-primary" />
      case 'done':
        return <CheckCircle className="h-4 w-4 text-green-500" />
      case 'error':
        return <AlertCircle className="h-4 w-4 text-destructive" />
      default:
        return null
    }
  })()

  const statusText = (() => {
    switch (item.status) {
      case 'pending':
        return t('notebooks.upload.status.pending')
      case 'uploading':
        return t('notebooks.upload.status.uploading')
      case 'done':
        return t('notebooks.upload.status.done')
      case 'error':
        return item.error || t('notebooks.upload.status.error')
      default:
        return ''
    }
  })()

  const iconSize = compact ? 'h-8 w-8' : 'w-10 h-10'
  const iconInner = compact ? 'h-4 w-4' : 'h-5 w-5'

  return (
    <div
      className={cn(
        'flex items-center gap-3 rounded-lg border p-2',
        compact && 'p-2',
        !compact && 'p-3',
        item.status === 'error' && 'border-destructive/30 bg-destructive/5',
        item.status === 'done' && 'border-green-200 bg-green-50/30',
      )}
    >
      {/* Preview or icon */}
      <div className={cn('flex-shrink-0 rounded-md bg-muted flex items-center justify-center overflow-hidden', iconSize)}>
        {item.previewUrl ? (
          <img
            src={item.previewUrl}
            alt={item.file.name}
            className="w-full h-full object-cover"
          />
        ) : (
          <Icon className={cn('text-muted-foreground', iconInner)} />
        )}
      </div>

      {/* Info */}
      <div className="flex-1 min-w-0">
        <p className={cn('font-medium truncate', compact ? 'text-xs' : 'text-sm')}>{item.file.name}</p>
        <div className="flex items-center gap-2 mt-0.5">
          {item.status === 'uploading' && (
            <Progress value={item.progress} className="h-1 flex-1" />
          )}
          <span className={cn('text-muted-foreground whitespace-nowrap', compact ? 'text-[10px]' : 'text-xs')}>
            {item.status === 'uploading'
              ? `${item.progress}%`
              : formatFileSize(item.file.size)}
          </span>
        </div>
        {item.status !== 'done' && (
          <p
            className={cn(
              compact ? 'text-[10px]' : 'text-xs',
              'mt-0.5',
              item.status === 'error' ? 'text-destructive' : 'text-muted-foreground',
            )}
          >
            {statusText}
          </p>
        )}
      </div>

      {/* Status icon */}
      <div className="flex-shrink-0">{statusIcon}</div>

      {/* Remove button (only for pending, done, error) */}
      {(item.status === 'pending' || item.status === 'done' || item.status === 'error') && (
        <Button
          variant="ghost"
          size="icon"
          className="flex-shrink-0 h-7 w-7"
          onClick={(e) => {
            e.stopPropagation()
            onRemove(item.id)
          }}
        >
          <X className="h-3.5 w-3.5" />
        </Button>
      )}
    </div>
  )
}

// ── Utility ────────────────────────────────────────────────────────────────

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`
}
